import { ChangeEvent, CSSProperties, FormEvent, useState } from 'react';
import { Button, Card, Icon } from '../components/ui';
import { usePublicBranding } from '../lib/publicBranding';
import { PUBLIC_API_BASE } from '../lib/api';
import PublicGhlChat from '../components/PublicGhlChat';
import { CareersFooter, brandVars } from './Careers';

const OUTCOMES = [
  { value: '52', label: 'automated jobs', detail: 'running the work your team used to chase' },
  { value: '24/7', label: 'operational coverage', detail: 'so momentum does not stop after hours' },
  { value: '1', label: 'connected system', detail: 'from first contact to placement-ready' },
];

const CAPABILITIES = [
  {
    number: '01',
    icon: 'user' as const,
    title: 'Recruiting that keeps moving',
    body: 'AI advances candidates, surfaces follow-ups, and keeps recruiters focused on conversations—not administrative drag.',
    items: ['Candidate pipeline', 'Job matching', 'Interview coordination'],
  },
  {
    number: '02',
    icon: 'shield' as const,
    title: 'Compliance without the chase',
    body: 'Credentials, documents, forms, and readiness live in one workflow with automation watching every deadline.',
    items: ['Credential tracking', 'Automated reminders', 'Placement readiness'],
  },
  {
    number: '03',
    icon: 'chart' as const,
    title: 'Leadership with a live view',
    body: 'See the health of your operation in real time—from recruiter execution to bottlenecks and business performance.',
    items: ['Executive dashboards', 'Team scorecards', 'Operational intelligence'],
  },
];

const WORKFLOW = [
  { icon: 'user' as const, label: 'Candidate enters' },
  { icon: 'sparkles' as const, label: 'AI orchestrates' },
  { icon: 'check-circle' as const, label: 'Team approves' },
  { icon: 'jobs' as const, label: 'Placement moves' },
];

const MONTHS = [
  ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
  ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
  ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
] as const;

interface DemoDateParts {
  month: string;
  day: string;
  year: string;
}

const EMPTY_DEMO_DATE: DemoDateParts = { month: '', day: '', year: '' };

interface DemoForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  team_size: string;
  preferred_time: string;
  challenge: string;
  consent: boolean;
  website: string;
}

const EMPTY_DEMO_FORM: DemoForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  role: '',
  team_size: '',
  preferred_time: '',
  challenge: '',
  consent: false,
  website: '',
};

function toLocalDateInputValue(date: Date): string {
  const localTime = date.getTime() - (date.getTimezoneOffset() * 60_000);
  return new Date(localTime).toISOString().slice(0, 10);
}

export default function LandingPage() {
  const brand = usePublicBranding();
  const [demoForm, setDemoForm] = useState<DemoForm>(EMPTY_DEMO_FORM);
  const [demoDate, setDemoDate] = useState<DemoDateParts>(EMPTY_DEMO_DATE);
  const [demoStatus, setDemoStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [demoMessage, setDemoMessage] = useState('');
  const now = new Date();
  const earliestDemoDate = toLocalDateInputValue(now);
  const latestDemo = new Date(now);
  latestDemo.setFullYear(latestDemo.getFullYear() + 1);
  const latestDemoDate = toLocalDateInputValue(latestDemo);
  const demoYears = Array.from(
    { length: latestDemo.getFullYear() - now.getFullYear() + 1 },
    (_, index) => String(now.getFullYear() + index),
  );
  const daysInDemoMonth = demoDate.month && demoDate.year
    ? new Date(Number(demoDate.year), Number(demoDate.month), 0).getDate()
    : 31;
  const demoDays = Array.from({ length: daysInDemoMonth }, (_, index) => String(index + 1).padStart(2, '0'));
  const selectedDemoDate = demoDate.month && demoDate.day && demoDate.year
    ? `${demoDate.year}-${demoDate.month}-${demoDate.day}`
    : '';

  const updateDemo = (field: keyof DemoForm) => (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;
    setDemoForm((current) => ({ ...current, [field]: value } as DemoForm));
  };

  const updateDemoDate = (field: keyof DemoDateParts) => (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setDemoDate((current) => {
      const next = { ...current, [field]: value };
      if (next.day && next.month && next.year) {
        const maxDay = new Date(Number(next.year), Number(next.month), 0).getDate();
        if (Number(next.day) > maxDay) next.day = '';
      }
      return next;
    });
  };

  const submitDemo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedDemoDate || selectedDemoDate < earliestDemoDate || selectedDemoDate > latestDemoDate) {
      setDemoStatus('error');
      setDemoMessage('Choose a valid date within the next 12 months.');
      return;
    }
    setDemoStatus('sending');
    setDemoMessage('');
    try {
      const response = await fetch(`${PUBLIC_API_BASE}/public/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...demoForm,
          preferred_date: selectedDemoDate,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        }),
      });
      const data = await response.json().catch(() => ({})) as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'We could not submit your request.');
      setDemoStatus('success');
      setDemoMessage(data.message || 'Your request was received. Our team will confirm your demo time.');
      setDemoForm(EMPTY_DEMO_FORM);
      setDemoDate(EMPTY_DEMO_DATE);
    } catch (error) {
      setDemoStatus('error');
      setDemoMessage((error as Error).message || 'We could not submit your request. Please try again.');
    }
  };

  return (
    <div className="bz-site" style={{ ...brandVars(brand.primary_color) }}>
      <header className="bz-header">
        <a href="/" className="bz-brand" aria-label={`${brand.app_name} home`}>
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="" className="bz-logo" />
          ) : (
            <span className="bz-mark" aria-hidden="true">B</span>
          )}
          <span>{brand.app_name}</span>
        </a>
        <nav className="bz-nav" aria-label="Main navigation">
          <a href="#difference">Why {brand.app_name}</a>
          <a href="#platform">Platform</a>
          <a href="#how-it-works">How it works</a>
        </nav>
        <div className="bz-header-actions">
          <Button href="/dashboard" variant="ghost" size="sm">Sign in</Button>
          <Button href="#contact" size="sm">Book a demo</Button>
        </div>
      </header>

      <main>
        <section className="bz-hero">
          <div className="bz-hero-image" aria-hidden="true" />
          <div className="bz-hero-grid" aria-hidden="true" />
          <div className="bz-hero-content">
            <div className="bz-kicker"><span /> The AI operating system for healthcare staffing</div>
            <h1>
              Your operation should not wait for someone to <em>push it forward.</em>
            </h1>
            <p className="bz-hero-copy">
              Meet {brand.app_name}—the intelligent operating layer that coordinates recruiting, compliance,
              onboarding, and leadership workflows while your team focuses on people and growth.
            </p>
            <div className="bz-hero-actions">
              <Button href="#contact" size="lg" icon={<Icon name="sparkles" size={17} />}>
                See {brand.app_name} in action
              </Button>
              <Button href="#platform" variant="secondary" size="lg">
                Explore the platform
              </Button>
            </div>
            <div className="bz-trust-row">
              <span><Icon name="check-circle" size={15} /> Built for healthcare staffing</span>
              <span><Icon name="check-circle" size={15} /> Enterprise-ready workflows</span>
              <span><Icon name="check-circle" size={15} /> Human control, AI speed</span>
            </div>
          </div>
          <div className="bz-scroll-cue" aria-hidden="true"><span /> Discover {brand.app_name}<span /></div>
        </section>

        <section id="difference" className="bz-proof">
          <div className="bz-section-inner">
            <div className="bz-proof-heading">
              <div>
                <div className="bz-eyebrow">The {brand.app_name} difference</div>
                <h2>Not another tool your team has to manage.</h2>
              </div>
              <p>
                Most software waits for a person to click the next button. {brand.app_name} understands the
                workflow, watches for what is next, and helps move the operation forward.
              </p>
            </div>
            <div className="bz-contrast">
              <div className="bz-contrast-card muted">
                <div className="bz-contrast-number">Before</div>
                <h3>Assisted work</h3>
                <p>A person still finds the task, remembers the deadline, and pushes every step—just with more tabs open.</p>
              </div>
              <div className="bz-contrast-arrow"><Icon name="sparkles" size={20} /></div>
              <div className="bz-contrast-card strong">
                <div className="bz-contrast-number">With {brand.app_name}</div>
                <h3>Orchestrated operations</h3>
                <p>The system watches, routes, reminds, and escalates on schedule, while your team stays in control.</p>
              </div>
            </div>
            <div className="bz-stats">
              {OUTCOMES.map((item) => (
                <div className="bz-stat" key={item.label}>
                  <div className="bz-stat-value">{item.value}</div>
                  <div>
                    <strong>{item.label}</strong>
                    <span>{item.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="platform" className="bz-platform">
          <div className="bz-orb bz-orb-one" aria-hidden="true" />
          <div className="bz-orb bz-orb-two" aria-hidden="true" />
          <div className="bz-section-inner">
            <div className="bz-section-heading centered">
              <div className="bz-eyebrow light">One intelligent operating layer</div>
              <h2>Built around the way staffing actually works.</h2>
              <p>{brand.app_name} connects the handoffs that cost staffing companies time, visibility, and placements.</p>
            </div>
            <div className="bz-capability-grid">
              {CAPABILITIES.map((item) => (
                <Card className="bz-capability-card" key={item.title}>
                  <div className="bz-card-top">
                    <span>{item.number}</span>
                    <div className="bz-icon"><Icon name={item.icon} size={20} /></div>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <ul>
                    {item.items.map((text) => (
                      <li key={text}><Icon name="check-circle" size={15} /> {text}</li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="bz-workflow">
          <div className="bz-section-inner bz-workflow-layout">
            <div className="bz-workflow-copy">
              <div className="bz-eyebrow">Always moving. Never out of control.</div>
              <h2>{brand.app_name} handles the motion. Your team owns the decisions.</h2>
              <p>
                Automation should create confidence, not mystery. Every action stays visible,
                every important decision stays human, and every handoff has a clear owner.
              </p>
              <Button href="#contact" variant="secondary" size="lg">Build your workflow</Button>
            </div>
            <div className="bz-workflow-panel">
              <div className="bz-panel-status"><span /> Live workflow</div>
              {WORKFLOW.map((item, index) => (
                <div className="bz-workflow-step" key={item.label}>
                  <div className="bz-step-icon"><Icon name={item.icon} size={18} /></div>
                  <div>
                    <small>Step {index + 1}</small>
                    <strong>{item.label}</strong>
                  </div>
                  {index < WORKFLOW.length - 1 && <div className="bz-step-line" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="bz-demo-section">
          <div className="bz-demo-glow" aria-hidden="true" />
          <div className="bz-section-inner bz-demo-layout">
            <div className="bz-demo-copy">
              <div className="bz-eyebrow light">Book a personalized demo</div>
              <h2>Show us where your operation gets stuck.</h2>
              <p>
                Pick a time that works for you and tell us what your team is trying to improve.
                We will tailor the conversation to your staffing workflow.
              </p>
              <div className="bz-demo-benefits">
                <span><Icon name="check-circle" size={16} /> A walkthrough built around your company</span>
                <span><Icon name="check-circle" size={16} /> Practical automation opportunities</span>
                <span><Icon name="check-circle" size={16} /> Clear next steps—no generic sales pitch</span>
              </div>
              <Button href="/dashboard" variant="secondary">Already a client? Sign in</Button>
            </div>

            <div className="bz-demo-card">
              {demoStatus === 'success' ? (
                <div className="bz-demo-success" role="status">
                  <div className="bz-success-icon"><Icon name="check-circle" size={28} /></div>
                  <h3>Your demo request is in.</h3>
                  <p>{demoMessage}</p>
                  <button type="button" onClick={() => setDemoStatus('idle')}>Book another time</button>
                </div>
              ) : (
                <form className="bz-demo-form" onSubmit={submitDemo}>
                  <div className="bz-form-heading">
                    <div>
                      <small>Personalized platform demo</small>
                      <h3>Choose your preferred time</h3>
                    </div>
                    <Icon name="calendar" size={22} />
                  </div>

                  <div className="bz-form-grid">
                    <label>
                      Your name
                      <input required autoComplete="name" value={demoForm.name} onChange={updateDemo('name')} placeholder="Jordan Smith" />
                    </label>
                    <label>
                      Work email
                      <input required type="email" autoComplete="email" value={demoForm.email} onChange={updateDemo('email')} placeholder="jordan@company.com" />
                    </label>
                    <label>
                      Company
                      <input required autoComplete="organization" value={demoForm.company} onChange={updateDemo('company')} placeholder="Your staffing company" />
                    </label>
                    <label>
                      Your role
                      <input required autoComplete="organization-title" value={demoForm.role} onChange={updateDemo('role')} placeholder="Owner, COO, recruiter…" />
                    </label>
                    <label>
                      Phone <span>Optional</span>
                      <input type="tel" autoComplete="tel" value={demoForm.phone} onChange={updateDemo('phone')} placeholder="(555) 555-0123" />
                    </label>
                    <label>
                      Team size
                      <select required value={demoForm.team_size} onChange={updateDemo('team_size')}>
                        <option value="">Select team size</option>
                        <option>1–5 people</option>
                        <option>6–15 people</option>
                        <option>16–50 people</option>
                        <option>51–100 people</option>
                        <option>101+ people</option>
                      </select>
                    </label>
                    <fieldset className="bz-date-field">
                      <legend>Preferred date</legend>
                      <div className="bz-date-selects">
                        <select required aria-label="Preferred month" value={demoDate.month} onChange={updateDemoDate('month')}>
                          <option value="">Month</option>
                          {MONTHS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <select required aria-label="Preferred day" value={demoDate.day} onChange={updateDemoDate('day')}>
                          <option value="">Day</option>
                          {demoDays.map((day) => <option key={day} value={day}>{Number(day)}</option>)}
                        </select>
                        <select required aria-label="Preferred year" value={demoDate.year} onChange={updateDemoDate('year')}>
                          <option value="">Year</option>
                          {demoYears.map((year) => <option key={year} value={year}>{year}</option>)}
                        </select>
                      </div>
                      <span>Month / Day / Year · next 12 months</span>
                    </fieldset>
                    <label>
                      Preferred time
                      <select required value={demoForm.preferred_time} onChange={updateDemo('preferred_time')}>
                        <option value="">Select a time</option>
                        <option>9:00 AM</option>
                        <option>10:30 AM</option>
                        <option>12:00 PM</option>
                        <option>1:30 PM</option>
                        <option>3:00 PM</option>
                        <option>4:30 PM</option>
                      </select>
                    </label>
                  </div>

                  <label className="bz-form-wide">
                    What would you most like to improve?
                    <textarea required minLength={10} rows={4} value={demoForm.challenge} onChange={updateDemo('challenge')} placeholder="Tell us about the bottleneck, manual work, or growth goal you want to solve." />
                  </label>

                  <label className="bz-honeypot" aria-hidden="true">
                    Website
                    <input tabIndex={-1} autoComplete="off" value={demoForm.website} onChange={updateDemo('website')} />
                  </label>

                  <label className="bz-consent">
                    <input required type="checkbox" checked={demoForm.consent} onChange={updateDemo('consent')} />
                    <span>I agree to be contacted about my demo request.</span>
                  </label>

                  {demoStatus === 'error' && <div className="bz-form-error" role="alert">{demoMessage}</div>}

                  <button className="bz-demo-submit" type="submit" disabled={demoStatus === 'sending'}>
                    {demoStatus === 'sending' ? 'Sending your request…' : <>Request my demo <Icon name="arrow-right" size={17} /></>}
                  </button>
                  <div className="bz-form-note">Your information is sent securely to our demo team.</div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <CareersFooter />
      <PublicGhlChat
        primaryColor={brand.primary_color}
        locationId={brand.ghl_chat_location_id}
        widgetId={brand.ghl_chat_widget_id}
      />

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  :root { scroll-behavior: smooth; }
  .bz-site {
    --navy: #071426; --navy-2: #0b2038; --blue: #123d65; --gold: #d6a52e;
    --gold-light: #f5ce72; --cyan: #56d8ff; --ink: #0a1b2e; --muted: #60738a;
    min-height: 100vh; background: #f6f8fb; color: var(--ink); overflow: hidden;
  }
  .bz-header {
    position: absolute; z-index: 20; top: 0; left: 0; right: 0; height: 78px;
    display: flex; align-items: center; justify-content: space-between; gap: 24px;
    max-width: 1240px; margin: 0 auto; padding: 0 24px;
    border-bottom: 1px solid rgba(255,255,255,.12);
  }
  .bz-brand { display:flex; align-items:center; gap:11px; color:#fff; text-decoration:none; font-size:17px; font-weight:800; letter-spacing:.2px; }
  .bz-logo { height:32px; width:auto; max-width:150px; object-fit:contain; }
  .bz-mark {
    width:34px; height:34px; border:1px solid rgba(245,206,114,.55); border-radius:10px;
    display:grid; place-items:center; color:var(--gold-light); font:700 20px/1 Georgia,serif;
    box-shadow:0 0 24px rgba(214,165,46,.16); background:rgba(255,255,255,.04);
  }
  .bz-nav { display:flex; align-items:center; gap:30px; }
  .bz-nav a { color:rgba(255,255,255,.72); text-decoration:none; font-size:13px; font-weight:650; transition:.2s ease; }
  .bz-nav a:hover { color:#fff; }
  .bz-header-actions { display:flex; align-items:center; gap:8px; }

  .bz-hero { min-height:760px; position:relative; display:flex; align-items:center; background:var(--navy); color:#fff; }
  .bz-hero::after {
    content:""; position:absolute; inset:0;
    background:linear-gradient(90deg,rgba(3,13,27,.98) 0%,rgba(5,18,35,.92) 34%,rgba(5,18,35,.42) 58%,rgba(5,18,35,.08) 100%);
  }
  .bz-hero-image { position:absolute; inset:0; background:url('/bz-ai-hero.svg') center/cover no-repeat; }
  .bz-hero-grid {
    position:absolute; inset:0; opacity:.12;
    background-image:linear-gradient(rgba(86,216,255,.16) 1px,transparent 1px),linear-gradient(90deg,rgba(86,216,255,.16) 1px,transparent 1px);
    background-size:72px 72px; mask-image:linear-gradient(90deg,#000,transparent 67%);
  }
  .bz-hero-content { position:relative; z-index:2; width:100%; max-width:1240px; margin:0 auto; padding:150px 24px 110px; }
  .bz-kicker, .bz-eyebrow {
    display:flex; align-items:center; gap:10px; color:#aa7600; font-size:12px; font-weight:800;
    letter-spacing:1.7px; text-transform:uppercase;
  }
  .bz-kicker { color:var(--gold-light); }
  .bz-kicker span { width:28px; height:1px; background:var(--gold); box-shadow:0 0 12px var(--gold); }
  .bz-hero h1 { margin:20px 0 0; max-width:710px; font:700 clamp(46px,5.9vw,76px)/.98 Georgia,"Times New Roman",serif; letter-spacing:-2.6px; }
  .bz-hero h1 em { color:var(--gold-light); font-style:normal; }
  .bz-hero-copy { max-width:620px; margin:25px 0 0; color:rgba(224,235,247,.78); font-size:18px; line-height:1.72; }
  .bz-hero-actions { margin-top:32px; display:flex; gap:12px; flex-wrap:wrap; }
  .bz-trust-row { display:flex; gap:22px; flex-wrap:wrap; margin-top:34px; color:rgba(255,255,255,.6); font-size:12px; }
  .bz-trust-row span { display:flex; align-items:center; gap:7px; }
  .bz-trust-row svg { color:var(--gold-light); }
  .bz-scroll-cue { position:absolute; z-index:3; bottom:26px; left:50%; transform:translateX(-50%); color:rgba(255,255,255,.72); font-size:10px; font-weight:750; letter-spacing:1.8px; text-transform:uppercase; display:flex; align-items:center; justify-content:center; gap:14px; white-space:nowrap; }
  .bz-scroll-cue span { width:48px; height:1px; flex:0 0 48px; background:linear-gradient(90deg,rgba(245,206,114,.15),rgba(245,206,114,.9)); box-shadow:0 0 10px rgba(214,165,46,.22); }
  .bz-scroll-cue span:last-child { background:linear-gradient(90deg,rgba(245,206,114,.9),rgba(245,206,114,.15)); }

  .bz-section-inner { max-width:1160px; margin:0 auto; position:relative; z-index:2; }
  .bz-proof { padding:106px 24px; background:#f7f9fc; }
  .bz-proof-heading { display:grid; grid-template-columns:1.1fr .8fr; gap:70px; align-items:end; }
  .bz-proof-heading h2, .bz-section-heading h2, .bz-workflow-copy h2, .bz-cta h2 {
    margin:13px 0 0; font:700 clamp(34px,4.5vw,54px)/1.04 Georgia,"Times New Roman",serif; letter-spacing:-1.3px;
  }
  .bz-proof-heading p, .bz-section-heading p, .bz-workflow-copy p { margin:0; color:var(--muted); font-size:16px; line-height:1.75; }
  .bz-contrast { display:grid; grid-template-columns:1fr 56px 1fr; align-items:stretch; margin-top:52px; }
  .bz-contrast-card { padding:34px; border:1px solid #dce3eb; background:#fff; border-radius:20px; }
  .bz-contrast-card.strong { color:#fff; border-color:rgba(86,216,255,.2); background:linear-gradient(135deg,#0b213a,#113b60); box-shadow:0 24px 65px rgba(8,30,53,.2); }
  .bz-contrast-number { color:#a67b1c; font-size:11px; font-weight:850; letter-spacing:1.5px; text-transform:uppercase; }
  .bz-contrast-card h3 { margin:13px 0 9px; font-size:22px; }
  .bz-contrast-card p { margin:0; color:var(--muted); line-height:1.65; }
  .bz-contrast-card.strong p { color:rgba(255,255,255,.68); }
  .bz-contrast-arrow { width:56px; height:56px; align-self:center; display:grid; place-items:center; z-index:2; margin:0 -1px; color:var(--gold); border:1px solid #dce3eb; border-radius:50%; background:#fff; }
  .bz-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:0; margin-top:54px; border-top:1px solid #dce3eb; border-bottom:1px solid #dce3eb; }
  .bz-stat { display:flex; gap:18px; align-items:center; padding:32px 28px; border-right:1px solid #dce3eb; }
  .bz-stat:last-child { border-right:0; }
  .bz-stat-value { color:#b68410; font:700 46px/1 Georgia,serif; }
  .bz-stat strong, .bz-stat span { display:block; }
  .bz-stat strong { font-size:14px; }
  .bz-stat span { margin-top:4px; color:var(--muted); font-size:12px; line-height:1.45; }

  .bz-platform { position:relative; padding:112px 24px; background:var(--navy); color:#fff; }
  .bz-platform::before { content:""; position:absolute; inset:0; opacity:.16; background-image:radial-gradient(circle at 1px 1px,rgba(86,216,255,.45) 1px,transparent 0); background-size:34px 34px; mask-image:linear-gradient(#000,transparent 85%); }
  .bz-orb { position:absolute; border-radius:50%; filter:blur(2px); opacity:.26; }
  .bz-orb-one { width:400px; height:400px; right:-180px; top:40px; background:radial-gradient(circle,rgba(86,216,255,.35),transparent 67%); }
  .bz-orb-two { width:300px; height:300px; left:-130px; bottom:-80px; background:radial-gradient(circle,rgba(214,165,46,.35),transparent 68%); }
  .bz-section-heading.centered { max-width:720px; margin:0 auto; text-align:center; }
  .bz-section-heading.centered .bz-eyebrow { justify-content:center; }
  .bz-eyebrow.light { color:var(--gold-light); }
  .bz-section-heading p { margin:18px auto 0; color:rgba(219,232,244,.65); max-width:620px; }
  .bz-capability-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin-top:54px; }
  .bz-capability-card { padding:30px !important; color:#fff !important; border:1px solid rgba(255,255,255,.11) !important; border-radius:18px !important; background:linear-gradient(145deg,rgba(255,255,255,.075),rgba(255,255,255,.025)) !important; backdrop-filter:blur(12px); transition:transform .25s ease,border-color .25s ease !important; }
  .bz-capability-card:hover { transform:translateY(-6px); border-color:rgba(245,206,114,.42) !important; }
  .bz-card-top { display:flex; justify-content:space-between; align-items:center; }
  .bz-card-top>span { width:42px; height:28px; display:grid; place-items:center; color:var(--gold-light); font:800 11px/1 system-ui,sans-serif; letter-spacing:1px; border:1px solid rgba(245,206,114,.3); border-radius:999px; background:rgba(214,165,46,.09); box-shadow:inset 0 0 16px rgba(214,165,46,.04); }
  .bz-icon { width:44px; height:44px; border-radius:13px; display:grid; place-items:center; color:var(--gold-light); border:1px solid rgba(245,206,114,.25); background:rgba(214,165,46,.09); }
  .bz-capability-card h3 { margin:26px 0 12px; font:700 25px/1.15 Georgia,serif; }
  .bz-capability-card p { margin:0; color:rgba(219,232,244,.64); line-height:1.67; font-size:14px; }
  .bz-capability-card ul { list-style:none; padding:22px 0 0; margin:24px 0 0; border-top:1px solid rgba(255,255,255,.1); display:grid; gap:11px; }
  .bz-capability-card li { display:flex; gap:8px; align-items:center; color:rgba(255,255,255,.8); font-size:13px; }
  .bz-capability-card li svg { color:var(--gold-light); }

  .bz-workflow { padding:112px 24px; background:linear-gradient(135deg,#f7f9fc,#eef3f8); }
  .bz-workflow-layout { display:grid; grid-template-columns:.9fr 1.1fr; gap:90px; align-items:center; }
  .bz-workflow-copy p { margin:20px 0 28px; }
  .bz-workflow-panel { position:relative; padding:28px; border-radius:22px; background:#fff; border:1px solid #d9e2eb; box-shadow:0 32px 80px rgba(15,44,72,.14); }
  .bz-panel-status { display:flex; align-items:center; gap:8px; padding-bottom:20px; border-bottom:1px solid #e4eaf0; color:#64778c; font-size:11px; font-weight:800; letter-spacing:1.3px; text-transform:uppercase; }
  .bz-panel-status span { width:8px; height:8px; border-radius:50%; background:#39cc91; box-shadow:0 0 0 5px rgba(57,204,145,.12); }
  .bz-workflow-step { position:relative; display:flex; align-items:center; gap:16px; padding:17px 0; }
  .bz-step-icon { width:44px; height:44px; flex:0 0 auto; border-radius:13px; display:grid; place-items:center; color:#b27e09; background:#fff8e7; border:1px solid #f0dca9; }
  .bz-workflow-step small, .bz-workflow-step strong { display:block; }
  .bz-workflow-step small { color:#8a9aaa; font-size:10px; text-transform:uppercase; letter-spacing:1px; }
  .bz-workflow-step strong { margin-top:4px; font-size:14px; }
  .bz-step-line { position:absolute; left:21px; top:61px; width:2px; height:18px; background:linear-gradient(#d8bd77,#dce5ed); }

  .bz-demo-section { position:relative; padding:110px 24px; color:#fff; background:linear-gradient(135deg,#071426,#0c2b49); overflow:hidden; }
  .bz-demo-section::before { content:""; position:absolute; width:720px; height:720px; left:-260px; top:-240px; border:1px solid rgba(86,216,255,.09); border-radius:50%; }
  .bz-demo-glow { position:absolute; width:520px; height:420px; right:-120px; bottom:-180px; background:rgba(59,172,235,.12); filter:blur(80px); }
  .bz-demo-layout { display:grid; grid-template-columns:minmax(0,.78fr) minmax(520px,1.22fr); gap:72px; align-items:center; }
  .bz-demo-copy h2 { margin:15px 0 0; font:700 clamp(38px,4.8vw,58px)/1.03 Georgia,"Times New Roman",serif; letter-spacing:-1.4px; }
  .bz-demo-copy>p { margin:22px 0 0; max-width:520px; color:rgba(224,235,247,.68); font-size:16px; line-height:1.72; }
  .bz-demo-benefits { display:grid; gap:13px; margin:28px 0 30px; color:rgba(255,255,255,.82); font-size:13px; }
  .bz-demo-benefits span { display:flex; align-items:center; gap:9px; }
  .bz-demo-benefits svg { color:var(--gold-light); }
  .bz-demo-card { position:relative; padding:30px; border:1px solid rgba(255,255,255,.14); border-radius:24px; background:rgba(255,255,255,.97); color:var(--ink); box-shadow:0 34px 90px rgba(0,0,0,.3); }
  .bz-form-heading { display:flex; align-items:center; justify-content:space-between; gap:20px; margin-bottom:24px; color:#b27e09; }
  .bz-form-heading small { color:#8a99a9; font-size:10px; font-weight:800; letter-spacing:1.3px; text-transform:uppercase; }
  .bz-form-heading h3 { margin:5px 0 0; color:var(--ink); font:700 25px/1.15 Georgia,serif; }
  .bz-form-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
  .bz-demo-form label { display:grid; gap:7px; color:#334a61; font-size:12px; font-weight:750; }
  .bz-demo-form label>span { color:#94a1ae; font-weight:500; }
  .bz-date-field { min-width:0; margin:0; padding:0; border:0; }
  .bz-date-field legend { margin:0 0 7px; padding:0; color:#334a61; font-size:12px; font-weight:750; }
  .bz-date-selects { display:grid; grid-template-columns:1.35fr .72fr .82fr; gap:8px; }
  .bz-date-field>span { display:block; margin-top:7px; color:#94a1ae; font-size:11px; font-weight:500; }
  .bz-demo-form input,.bz-demo-form select,.bz-demo-form textarea { width:100%; box-sizing:border-box; border:1px solid #d7e0e8; border-radius:10px; padding:12px 13px; background:#f9fbfd; color:#10243a; font:500 13px system-ui,sans-serif; outline:none; transition:border-color .2s,box-shadow .2s; }
  .bz-demo-form input:focus,.bz-demo-form select:focus,.bz-demo-form textarea:focus { border-color:#4a87bd; box-shadow:0 0 0 3px rgba(74,135,189,.13); background:#fff; }
  .bz-demo-form textarea { resize:vertical; line-height:1.5; }
  .bz-form-wide { margin-top:16px; }
  .bz-honeypot { position:absolute !important; left:-9999px !important; width:1px !important; height:1px !important; overflow:hidden !important; }
  .bz-consent { display:flex !important; grid-template-columns:18px 1fr; align-items:start; gap:9px !important; margin-top:17px; color:#5f7184 !important; font-weight:500 !important; line-height:1.45; }
  .bz-consent input { width:16px; height:16px; margin:1px 0 0; accent-color:#b27e09; }
  .bz-consent span { color:#5f7184 !important; }
  .bz-demo-submit { width:100%; margin-top:18px; padding:14px 18px; border:0; border-radius:11px; display:flex; align-items:center; justify-content:center; gap:9px; color:#fff; background:linear-gradient(135deg,#315f94,#5477d9); font-weight:800; cursor:pointer; box-shadow:0 12px 25px rgba(49,95,148,.24); }
  .bz-demo-submit:hover { filter:brightness(1.06); transform:translateY(-1px); }
  .bz-demo-submit:disabled { opacity:.65; cursor:wait; transform:none; }
  .bz-form-note { margin-top:10px; color:#8b9aaa; font-size:10px; text-align:center; }
  .bz-form-error { margin-top:14px; padding:10px 12px; border-radius:9px; color:#991b1b; background:#fef2f2; border:1px solid #fecaca; font-size:12px; }
  .bz-demo-success { min-height:430px; display:grid; place-content:center; justify-items:center; text-align:center; padding:20px; }
  .bz-success-icon { width:60px; height:60px; display:grid; place-items:center; border-radius:50%; color:#16825d; background:#e8faf3; border:1px solid #b9ecd9; }
  .bz-demo-success h3 { margin:22px 0 8px; font:700 30px Georgia,serif; }
  .bz-demo-success p { margin:0; max-width:390px; color:var(--muted); line-height:1.65; }
  .bz-demo-success button { margin-top:24px; border:0; background:none; color:#315f94; font-weight:750; cursor:pointer; }

  @media (max-width: 900px) {
    .bz-nav { display:none; }
    .bz-hero { min-height:740px; }
    .bz-hero-image { background-position:62% center; opacity:.52; }
    .bz-hero::after { background:linear-gradient(90deg,rgba(3,13,27,.98),rgba(5,18,35,.84)); }
    .bz-proof-heading,.bz-workflow-layout,.bz-demo-layout { grid-template-columns:1fr; gap:38px; }
    .bz-demo-layout { max-width:720px; }
    .bz-capability-grid { grid-template-columns:1fr; }
    .bz-stats { grid-template-columns:1fr; }
    .bz-stat { border-right:0; border-bottom:1px solid #dce3eb; }
    .bz-stat:last-child { border-bottom:0; }
  }
  @media (max-width: 640px) {
    .bz-header { height:68px; padding:0 16px; }
    .bz-header-actions>a:first-child { display:none !important; }
    .bz-hero { min-height:760px; align-items:flex-end; }
    .bz-hero-image { background-position:68% top; background-size:auto 62%; background-repeat:no-repeat; }
    .bz-hero::after { background:linear-gradient(0deg,#061426 43%,rgba(6,20,38,.42) 100%); }
    .bz-hero-content { padding:280px 18px 78px; }
    .bz-hero h1 { font-size:42px; letter-spacing:-1.5px; }
    .bz-hero-copy { font-size:15px; }
    .bz-trust-row { display:none; }
    .bz-scroll-cue { display:none; }
    .bz-proof,.bz-platform,.bz-workflow,.bz-demo-section { padding:76px 18px; }
    .bz-demo-card { padding:22px; }
    .bz-form-grid { grid-template-columns:1fr; }
    .bz-date-selects { grid-template-columns:1.2fr .7fr .8fr; }
    .bz-contrast { grid-template-columns:1fr; gap:12px; }
    .bz-contrast-arrow { margin:-27px auto; transform:rotate(90deg); }
    .bz-contrast-card { padding:25px; }
    .bz-proof-heading h2,.bz-section-heading h2,.bz-workflow-copy h2,.bz-cta h2 { font-size:36px; }
    .bz-stat { padding:24px 8px; }
  }
`;

const _styleTypeCheck: CSSProperties = {};
void _styleTypeCheck;
